import React from 'react'
import { useNavigationStore } from '@/stores/navigation'

interface PrivacyPolicyModalProps {
  isOpen: boolean
  onClose: () => void
}

const PrivacyPolicyModal: React.FC<PrivacyPolicyModalProps> = ({ isOpen, onClose }) => {
  const navigationStore = useNavigationStore()

  const handlePlatformPrivacyClick = () => {
    onClose() // Close current modal
    navigationStore.openPlatformPrivacyPolicyModal() // Open platform privacy policy modal
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-3xl font-bold text-gray-900">Website Privacy Policy</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Divider */}
          <div className="border-b border-blue-200 mb-6"></div>

          {/* Content */}
          <div className="prose prose-gray max-w-none">
            <p className="text-sm text-gray-600 mb-6">
              <strong>Effective Date:</strong> September 22, 2025
            </p>

            <p className="text-gray-700 mb-6">
              <strong>⚠️ Scope:</strong> This Website Privacy Policy applies only to visitors of our corporate website. 
              It does not apply to data collected through our SaaS platform, which is governed separately by our 
              <button 
                onClick={handlePlatformPrivacyClick}
                className="bg-yellow-200 px-1 rounded hover:bg-yellow-300 transition-colors cursor-pointer underline"
              >
                Platform Privacy Policy and Data Privacy Framework Statement
              </button>.
            </p>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">1. Information We Collect</h3>
              <p className="text-gray-700 mb-4">
                Information you provide (e.g., name, email), automatic data (IP, browser, device), and cookies/analytics.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">2. How We Use Information</h3>
              <p className="text-gray-700 mb-4">
                To respond to inquiries, improve website performance, and comply with legal obligations.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">3. Sharing Information</h3>
              <p className="text-gray-700 mb-4">
                With service providers, authorities when required by law. No selling of personal information.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">4. International Transfers</h3>
              <p className="text-gray-700 mb-4">
                Our website is operated in the U.S. We participate in the EU-U.S. Data Privacy Framework (DPF) for SaaS platform services.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">5. Your Privacy Rights</h3>
              <p className="text-gray-700 mb-4">
                Depending on where you live, you may have rights under applicable data protection laws, including:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-6">
                <li>Right to access, correct, or delete your personal information.</li>
                <li>Right to object to or restrict processing.</li>
                <li>Right to data portability.</li>
                <li>Right to withdraw consent (where consent is the legal basis).</li>
                <li>Right to lodge a complaint with a supervisory authority (EEA/UK/Switzerland).</li>
              </ul>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Notice to California Residents</h3>
              <p className="text-gray-700 mb-4">
                If you are a California resident, you may have rights under the California Consumer Privacy Act (CCPA), 
                as amended by the California Privacy Rights Act (CPRA). These rights may include:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4 ml-6">
                <li>The right to know what categories of personal information we collect and how we use it.</li>
                <li>The right to request deletion of your personal information, subject to certain exceptions.</li>
                <li>The right to correct inaccurate personal information.</li>
                <li>The right to opt out of the "sale" or "sharing" of personal information (note: we do not sell personal information).</li>
                <li>The right to non-discrimination for exercising your privacy rights.</li>
              </ul>
              <p className="text-gray-700 mb-4">
                <strong>Residents of other jurisdictions, including Canada, may also have rights to access and correct their personal information under applicable laws.</strong>
              </p>
              <p className="text-gray-700">
                To exercise your rights, please contact us at <a href="mailto:management@eceptionist.com" className="text-blue-600 underline hover:text-blue-800">management@eceptionist.com</a>
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">6. Children's Privacy</h3>
              <p className="text-gray-700 mb-4">
                Not directed to children under 16.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">7. Security & Retention</h3>
              <p className="text-gray-700 mb-4">
                We safeguard your information with technical and organizational measures.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">8. Links</h3>
              <p className="text-gray-700 mb-4">
                Our website may link to third-party sites. We are not responsible for their policies.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">9. Changes</h3>
              <p className="text-gray-700 mb-4">
                We may update this policy and will indicate a revised effective date.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">10. Contact Us</h3>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-gray-700 mb-2">
                  <strong>Eceptionist</strong>
                </p>
                <p className="text-gray-700 mb-2">
                  7000 N. Mopac Expressway, Suite 200 – 1312, Austin, Texas 78731
                </p>
                <p className="text-gray-700 mb-2">
                  <strong>Email:</strong> <a href="mailto:management@eceptionist.com" className="text-blue-600 underline hover:text-blue-800">management@eceptionist.com</a>
                </p>
                <p className="text-gray-700">
                  <strong>Phone:</strong> 713-520-6688
                </p>
              </div>
            </section>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 pt-6 mt-8">
            <button
              onClick={onClose}
              className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-blue-600 rounded-md hover:bg-blue-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PrivacyPolicyModal
